import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft, Heart, Copy, Save, Sparkles, LifeBuoy, ImageIcon, CheckCircle2,
  Clock3, AlertTriangle, Wand2, ExternalLink, Compass, RotateCcw, Trash2, Star, Coins, Loader2, ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/app/AppShell";
import { PromptField } from "@/components/app/PromptField";
import { AiActionButton } from "@/components/app/AiActionButton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { libApi, aiApi, creditsApi, eventsApi, assistantApi, apiError } from "@/lib/api";
import { useCredits } from "@/context/CreditsContext";
import { validateAll, fieldDisplayValue } from "@/lib/promptEngine";
import { initialValues } from "@/lib/promptEngine";
import { CREATOR, AI } from "@/constants/testIds";

function outputLabel(mode) {
  const m = (mode || "").toLowerCase();
  if (/(_edit|correction|comparison|testimonial_edit)/.test(m)) return "Edycja / korekta zdjęcia";
  if (/(portrait|visual|photo|shot_list|identity_audit)/.test(m)) return "Zdjęcie / wizualizacja AI";
  if (/(video|_script|speech|webinar)/.test(m)) return "Scenariusz / wideo";
  return "Gotowy tekst do skopiowania";
}

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return true;
  } catch (e) {
    return false;
  }
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return "";
  }
}

export default function PromptCreator() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { balance, refresh: refreshCredits, openBuy } = useCredits();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null); // { prompt, repairPrompt, cost, balance }
  const [generating, setGenerating] = useState(false);
  const [aiOutput, setAiOutput] = useState("");
  const [favorite, setFavorite] = useState(false);
  const [whereOpen, setWhereOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [genCost, setGenCost] = useState(1);
  const fileInputRef = useRef(null);
  const resultRef = useRef(null);

  // Dyskretna pomoc w formularzu — tylko gdy użytkownik realnie utknął.
  const [helpReason, setHelpReason] = useState(null); // "errors" | "field" | "dwell"
  const [helpDismissed, setHelpDismissed] = useState(false);
  const [showExamples, setShowExamples] = useState(false);
  const failedAttemptsRef = useRef(0);
  const fieldChangesRef = useRef({});
  const dwellRef = useRef(null);
  const dismissedRef = useRef(false);
  const lastFieldRef = useRef(null);
  const focusedFieldRef = useRef(null);
  const hasFocusRef = useRef(false);
  const [fieldHelp, setFieldHelp] = useState(null); // {loading,error,field_label,explanation,examples,field_id}

  // Śledzenie pola z fokusem (kursorem) — ma pierwszeństwo w „Pomóż mi wybrać".
  const onFormFocus = (e) => {
    const tid = e.target?.getAttribute?.("data-testid") || "";
    if (tid.startsWith("prompt-input-")) {
      focusedFieldRef.current = tid.slice("prompt-input-".length);
      hasFocusRef.current = true;
    }
  };
  const onFormBlur = (e) => {
    const rtid = e.relatedTarget?.getAttribute?.("data-testid") || "";
    if (!rtid.startsWith("prompt-input-")) hasFocusRef.current = false;
  };

  const triggerHelp = (reason) => {
    if (dismissedRef.current) return;
    setHelpReason((r) => r || reason);
  };

  // Dwell = 45 s BEZ aktywności (pisanie, klik, scroll, zmiana pola). Sama lektura nie liczy się jako utknięcie.
  const armDwell = () => {
    if (dismissedRef.current) return;
    if (dwellRef.current) clearTimeout(dwellRef.current);
    dwellRef.current = setTimeout(() => triggerHelp("dwell"), 45000);
  };

  useEffect(() => {
    let active = true;
    setStatus("loading");
    setResult(null);
    setImagePreview(null);
    setAiOutput("");
    libApi
      .prompt(slug)
      .then((d) => {
        if (!active) return;
        setData(d);
        const base = initialValues(d.fields || []);
        const prefill = location.state?.prefill && location.state?.values;
        if (prefill) {
          const ids = new Set((d.fields || []).map((f) => f.id));
          for (const [k, v] of Object.entries(location.state.values)) {
            if (ids.has(k)) base[k] = v;
          }
        }
        setValues(base);
        setFavorite(!!d.is_favorite);
        setErrors({});
        setStatus("ok");
        // reset stanu pomocy przy zmianie promptu
        setHelpReason(null);
        setHelpDismissed(false);
        setShowExamples(false);
        setFieldHelp(null);
        failedAttemptsRef.current = 0;
        fieldChangesRef.current = {};
        dismissedRef.current = false;
        lastFieldRef.current = null;
        if (dwellRef.current) { clearTimeout(dwellRef.current); dwellRef.current = null; }
        if (prefill) toast("Wczytano parametry z zapisanej wersji.");
      })
      .catch((err) => active && setStatus(err.response?.status === 403 ? "forbidden" : "error"));
    libApi.promptHistory(slug).then((r) => active && setHistory(r.history || [])).catch(() => {});
    creditsApi.actionPrices().then((p) => active && setGenCost((p.prices || p)?.generate_personalized_prompt ?? 1)).catch(() => {});
    return () => {
      active = false;
    };
  }, [slug]);

  // Nasłuch aktywności: karta „dwell" pojawia się tylko po 45 s BEZ pisania/kliknięć/scrolla/zmiany pola.
  useEffect(() => {
    if (status !== "ok" || result || helpReason || helpDismissed) return;
    armDwell();
    const onActivity = () => armDwell();
    const events = ["keydown", "pointerdown", "mousedown", "wheel", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      if (dwellRef.current) { clearTimeout(dwellRef.current); dwellRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, result, helpReason, helpDismissed]);

  const fields = data?.fields || [];
  const balanceAfter = Math.max(0, (balance ?? 0) - genCost);

  const setField = (id, val) => {
    setValues((v) => ({ ...v, [id]: val }));
    if (errors[id]) setErrors((e) => ({ ...e, [id]: undefined }));
    // Zmiana danych unieważnia poprzedni wynik — ponowne generowanie to nowa operacja.
    if (result) setResult(null);
    lastFieldRef.current = id;
    // Heurystyka: wielokrotna zmiana jednego pola = możliwe utknięcie.
    const c = (fieldChangesRef.current[id] || 0) + 1;
    fieldChangesRef.current[id] = c;
    if (c >= 5) triggerHelp("field");
    // Aktywność resetuje licznik bezczynności.
    armDwell();
  };

  const handleGenerate = async () => {
    const errs = validateAll(fields, values, data?.required_groups || []);
    setErrors(errs);
    if (Object.keys(errs).filter((k) => errs[k]).length > 0) {
      toast.error("Uzupełnij zaznaczone pola, aby wygenerować prompt.");
      // Heurystyka: powtórzony błąd walidacji = użytkownik utknął.
      failedAttemptsRef.current += 1;
      if (failedAttemptsRef.current >= 2) triggerHelp("errors");
      return;
    }
    if ((balance ?? 0) < genCost) {
      toast.error("Za mało kredytów, aby wygenerować prompt.");
      openBuy();
      return;
    }
    setGenerating(true);
    try {
      const res = await aiApi.generatePrompt({ slug, values });
      setResult(res);
      if (dwellRef.current) { clearTimeout(dwellRef.current); dwellRef.current = null; }
      setHelpReason(null);
      await refreshCredits();
      toast.success(`Wykorzystano ${res.cost} kr. Pozostało Ci ${res.balance} kr.`);
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (err) {
      const st = err.response?.status;
      const detail = err.response?.data?.detail;
      if (st === 422 && detail?.fieldErrors) {
        setErrors(detail.fieldErrors);
        toast.error("Uzupełnij zaznaczone pola, aby wygenerować prompt.");
      } else if (st === 402) {
        toast.error("Za mało kredytów, aby wygenerować prompt.");
        openBuy();
      } else if (st === 502) {
        toast.success("Kredyt zwrócony — nie udało się złożyć promptu. Spróbuj ponownie.");
        await refreshCredits();
      } else {
        toast.error(apiError(typeof detail === "string" ? detail : null, "Nie udało się wygenerować promptu."));
      }
    } finally {
      setGenerating(false);
    }
  };

  // Pomoc dla KONKRETNEGO pola: wyjaśnienie kroku + max 3 przykłady. Bez wpisywania, bez zmiany pól.
  const pickHelpField = () => {
    // 1) Pole z aktualnym fokusem (kursorem) ma pierwszeństwo.
    if (hasFocusRef.current && focusedFieldRef.current) {
      const ff = fields.find((f) => f.id === focusedFieldRef.current);
      if (ff) return ff;
    }
    // 2) Kolejność awaryjna: błąd → ostatnio zmieniane → pierwsze z opisem → pierwsze.
    const errored = fields.find((f) => errors[f.id]);
    if (errored) return errored;
    if (lastFieldRef.current) {
      const lf = fields.find((f) => f.id === lastFieldRef.current);
      if (lf) return lf;
    }
    return fields.find((f) => f.help || f.placeholder || (f.options || []).length) || fields[0] || null;
  };

  const askFieldHelp = async () => {
    const target = pickHelpField();
    if (!target) return;
    setFieldHelp({ loading: true, field_id: target.id, field_label: target.label });
    try {
      const res = await assistantApi.fieldHelp(slug, target.id, String(values[target.id] ?? ""));
      setFieldHelp({ loading: false, ...res });
    } catch (err) {
      const st = err.response?.status;
      setFieldHelp({
        loading: false, field_id: target.id, field_label: target.label,
        error: st === 502
          ? "Asystent jest chwilowo niedostępny. Spróbuj ponownie za chwilę."
          : "Nie udało się pobrać podpowiedzi. Spróbuj ponownie.",
      });
    }
  };

  const handleCopy = async () => {
    const ok = await copyText(result?.prompt || "");
    if (ok) toast.success("Prompt skopiowany do schowka.");
    else toast.error("Nie udało się skopiować. Zaznacz tekst ręcznie.");
  };

  const copyAndOpenTool = async (url, name, toolId) => {
    const ok = await copyText(result?.prompt || "");
    if (ok) toast.success(`Prompt skopiowany — otwieram ${name}.`);
    else toast.error("Nie udało się skopiować. Zaznacz tekst ręcznie.");
    eventsApi.toolOpen(toolId || name, requiresImage ? "image" : "text", name);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyOpenHowto = async (url, name, toolId) => {
    const ok = await copyText(result?.prompt || "");
    if (ok) toast.success("Prompt skopiowany — wklej go w otwartym narzędziu.");
    else toast.error("Nie udało się skopiować. Zaznacz tekst ręcznie.");
    eventsApi.toolOpen(toolId || name, requiresImage ? "image" : "text", name);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const saveHistory = async (variant, text) => {
    try {
      const entry = await libApi.saveHistory(slug, { values, compiled_text: text, variant });
      setHistory((h) => [entry, ...h].slice(0, 20));
      toast.success(variant === "rescue" ? "Zapisano instrukcję poprawy w bibliotece." : "Zapisano prompt w bibliotece.");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail, "Nie udało się zapisać w bibliotece."));
    }
  };

  const toggleFavorite = async () => {
    const prev = favorite;
    setFavorite(!prev);
    try {
      if (prev) await libApi.removeFavorite(slug);
      else await libApi.addFavorite(slug);
    } catch (err) {
      setFavorite(prev);
      toast.error(apiError(err.response?.data?.detail, "Nie udało się zaktualizować ulubionych."));
    }
  };

  const onPickImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    toast("Zdjęcie zostaje na Twoim urządzeniu — nie wysyłamy go na serwer.");
  };
  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (status === "loading") {
    return (
      <AppShell>
        <div className="mx-auto max-w-content">
          <div className="h-9 w-72 animate-pulse rounded bg-brand-surface" />
          <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
            <div className="h-96 animate-pulse rounded-panel bg-brand-surface" />
            <div className="h-96 animate-pulse rounded-panel bg-brand-surface" />
          </div>
        </div>
      </AppShell>
    );
  }

  if (status === "forbidden") {
    return (
      <AppShell>
        <div data-testid="prompt-forbidden" className="mx-auto max-w-content rounded-panel border border-brand-line bg-brand-surface px-6 py-10">
          <h1 className="font-display text-2xl font-semibold text-brand-text">Brak dostępu do tego promptu</h1>
          <p className="mt-2 text-brand-sec">Ten prompt należy do pakietu, którego nie masz aktywnego na koncie.</p>
          <Link to="/biblioteka" className="mt-5 inline-flex items-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on">
            Wróć do biblioteki
          </Link>
        </div>
      </AppShell>
    );
  }

  if (status === "error" || !data) {
    return (
      <AppShell>
        <div className="mx-auto max-w-content rounded-card border border-red-500/40 bg-red-500/10 px-5 py-6 text-sm text-red-300">
          <p>Nie udało się wczytać promptu. Sprawdź połączenie i spróbuj ponownie.</p>
          <button
            type="button"
            data-testid="prompt-creator-retry"
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex items-center gap-2 rounded-button bg-brand-accent px-4 py-2 text-sm font-semibold text-brand-on"
          >
            Spróbuj ponownie
          </button>
        </div>
      </AppShell>
    );
  }

  const tool = data.tool_instruction || {};
  const requiresImage = !!tool.requires_image;
  const usageNote = data.usage_note;
  const recs = tool.recommendations || [];
  const howto = tool.howto || [];
  const claudeNote = tool.claude_note;
  const openTools = recs.filter((r) => r.url).slice(0, 2);
  const backTo = `/biblioteka/${data.package_slug}`;

  return (
    <AppShell>
      <div className="mx-auto max-w-content" data-testid={CREATOR.root}>
        <button
          type="button"
          data-testid={CREATOR.backLink}
          onClick={() => navigate(backTo)}
          className="mb-4 inline-flex items-center gap-2 text-sm text-brand-muted transition-colors hover:text-brand-text"
        >
          <ArrowLeft size={16} strokeWidth={2} /> Wróć do pakietu
        </button>

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {data.capabilities?.requiresVisualizationLabel && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-accent/15 px-2.5 py-1 text-xs font-semibold text-brand-accent">
                  WIZUALIZACJA
                </span>
              )}
            </div>
            <h1 data-testid={CREATOR.title} className="mt-3 font-display text-3xl font-semibold text-brand-text sm:text-4xl">
              {data.name}
            </h1>
            {data.short_benefit && <p className="mt-2 max-w-2xl text-brand-sec">{data.short_benefit}</p>}
            <p data-testid="creator-efekt" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand-elevated px-3 py-1 text-xs font-semibold text-brand-text">
              <Sparkles size={13} strokeWidth={2} className="text-brand-accent" />
              Efekt: {outputLabel(data.mode)}
            </p>
          </div>
          <button
            type="button"
            data-testid={CREATOR.favoriteButton}
            onClick={toggleFavorite}
            aria-label={favorite ? "Usuń z ulubionych" : "Dodaj do ulubionych"}
            className={`inline-flex shrink-0 items-center gap-2 rounded-button border px-4 py-2.5 text-sm font-semibold transition-colors ${
              favorite ? "border-brand-accent/60 text-brand-accent" : "border-brand-line text-brand-text hover:border-brand-accent/60"
            }`}
          >
            <Heart size={16} strokeWidth={1.75} fill={favorite ? "currentColor" : "none"} />
            {favorite ? "W ulubionych" : "Do ulubionych"}
          </button>
        </div>

        {/* Main grid */}
        <div className="mt-8 grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]">
          {/* Left: form */}
          <div className="min-w-0 space-y-6">
            {requiresImage && (
              <div className="rounded-panel border border-brand-line bg-brand-surface p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-300">
                    <ImageIcon size={18} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-[15px] font-semibold text-brand-text">Przygotuj zdjęcie</h3>
                    <p className="mt-1 text-[13px] text-brand-sec">
                      Po wygenerowaniu promptu dodasz zdjęcie bezpośrednio w ChatGPT, Gemini lub innym wybranym narzędziu. <strong className="text-brand-text">Promptivo nie przechowuje zdjęcia.</strong> Tu możesz podejrzeć plik lokalnie.
                    </p>
                    <input ref={fileInputRef} data-testid={CREATOR.imageInput} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
                    {!imagePreview ? (
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-field border border-brand-line px-3 py-2 text-xs font-medium text-brand-text transition-colors hover:border-brand-accent/60"
                      >
                        <ImageIcon size={14} strokeWidth={1.75} /> Wybierz zdjęcie (podgląd)
                      </button>
                    ) : (
                      <div className="mt-3">
                        <img data-testid="prompt-image-preview" src={imagePreview} alt="Podgląd zdjęcia" className="max-h-56 w-full rounded-card border border-brand-line object-contain" />
                        <div className="mt-2 flex gap-2">
                          <button
                            type="button"
                            data-testid="prompt-image-change"
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-1.5 rounded-field border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-text transition-colors hover:border-brand-accent/60"
                          >
                            <RotateCcw size={13} strokeWidth={1.75} /> Zmień zdjęcie
                          </button>
                          <button
                            type="button"
                            data-testid="prompt-image-remove"
                            onClick={removeImage}
                            className="inline-flex items-center gap-1.5 rounded-field border border-brand-line px-3 py-1.5 text-xs font-medium text-brand-sec transition-colors hover:border-red-400/60 hover:text-red-300"
                          >
                            <Trash2 size={13} strokeWidth={1.75} /> Usuń
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div data-testid={CREATOR.form} onFocus={onFormFocus} onBlur={onFormBlur} className="rounded-panel border border-brand-line bg-brand-surface p-5 sm:p-6">
              <h2 className="font-display text-lg font-semibold text-brand-text">Uzupełnij szczegóły</h2>
              <p className="mt-1 text-sm text-brand-sec">Pola z gwiazdką są wymagane. Prompt przygotujemy na podstawie Twoich odpowiedzi.</p>
              <div className="mt-5 space-y-5">
                {fields.map((f) => (
                  <PromptField key={f.id} field={f} value={values[f.id]} error={errors[f.id]} onChange={(v) => setField(f.id, v)} />
                ))}
                {fields.length === 0 && (
                  <p className="text-sm text-brand-muted">Ten prompt nie wymaga dodatkowych danych — po prostu go wygeneruj.</p>
                )}
              </div>

              {/* Dyskretna pomoc — pojawia się tylko, gdy użytkownik realnie utknął */}
              {helpReason && !helpDismissed && !result && (
                <div data-testid="inform-help-card" className="mt-5 rounded-card border border-brand-accent/40 bg-brand-accent/5 p-4">
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-accent/15 text-brand-accent">
                      <LifeBuoy size={17} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-brand-text">Potrzebujesz pomocy z tym krokiem?</p>
                      <p className="mt-1 text-[13px] text-brand-sec">
                        {helpReason === "errors" && "Formularz jeszcze nie przechodzi. Mogę pokazać przykład wypełnienia albo pomóc dobrać właściwy kreator."}
                        {helpReason === "field" && "Widzę, że kilka razy zmieniasz to samo pole. Mogę pokazać przykład wypełnienia — nie wpiszę danych za Ciebie."}
                        {helpReason === "dwell" && "Jeśli utknąłeś na którymś polu, chętnie pomogę — bez wpisywania danych za Ciebie."}
                      </p>

                      {showExamples && (
                        <ul data-testid="inform-help-examples" className="mt-3 space-y-2 rounded-field border border-brand-line bg-brand-bg p-3 text-[12.5px]">
                          {fields
                            .filter((f) => f.placeholder || f.help)
                            .slice(0, 4)
                            .map((f) => (
                              <li key={f.id}>
                                <span className="font-medium text-brand-text">{f.label}:</span>{" "}
                                <span className="text-brand-sec">{f.placeholder || f.help}</span>
                              </li>
                            ))}
                          {fields.filter((f) => f.placeholder || f.help).length === 0 && (
                            <li className="text-brand-muted">Brak przykładów dla tego formularza.</li>
                          )}
                        </ul>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          data-testid="inform-help-example"
                          onClick={() => setShowExamples((v) => !v)}
                          className="rounded-button border border-brand-line px-3.5 py-2 text-[13px] font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                        >
                          {showExamples ? "Ukryj przykład" : "Pokaż przykład"}
                        </button>
                        <button
                          type="button"
                          data-testid="inform-help-choose"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={askFieldHelp}
                          className="rounded-button border border-brand-line px-3.5 py-2 text-[13px] font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                        >
                          Pomóż mi wybrać
                        </button>
                        <button
                          type="button"
                          data-testid="inform-help-dismiss"
                          onClick={() => { setHelpDismissed(true); dismissedRef.current = true; setFieldHelp(null); }}
                          className="rounded-button px-3.5 py-2 text-[13px] font-semibold text-brand-sec transition-colors hover:text-brand-text"
                        >
                          Nie teraz
                        </button>
                      </div>

                      {fieldHelp && (
                        <div data-testid="inform-field-help" className="mt-3 rounded-field border border-brand-line bg-brand-bg p-3 text-[12.5px]">
                          {fieldHelp.loading ? (
                            <p className="inline-flex items-center gap-2 text-brand-sec">
                              <Loader2 size={14} className="animate-spin" /> Przygotowuję podpowiedź dla pola „{fieldHelp.field_label}”…
                            </p>
                          ) : fieldHelp.error ? (
                            <div className="space-y-2">
                              <p className="text-brand-sec">{fieldHelp.error}</p>
                              <button
                                type="button"
                                data-testid="inform-field-help-retry"
                                onClick={askFieldHelp}
                                className="inline-flex items-center gap-1.5 rounded-button border border-brand-line px-3 py-1.5 text-[12.5px] font-semibold text-brand-text hover:border-brand-accent/60"
                              >
                                <RotateCcw size={13} /> Spróbuj ponownie
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <p className="font-semibold text-brand-text">Pole „{fieldHelp.field_label}”</p>
                              {fieldHelp.explanation && <p className="text-brand-sec">{fieldHelp.explanation}</p>}
                              {(fieldHelp.examples || []).length > 0 && (
                                <div>
                                  <p className="text-brand-muted">Przykłady (tylko inspiracja — wpisujesz sam):</p>
                                  <ul className="mt-1 list-disc space-y-1 pl-5 text-brand-sec">
                                    {fieldHelp.examples.map((ex, i) => (
                                      <li key={i}>{ex}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: quote / generate (sticky, offset clears the topbar) */}
          <div className="rounded-panel border border-brand-line bg-brand-surface p-5 xl:sticky xl:top-20">
            {!result ? (
              <>
                <h3 className="font-display text-[15px] font-semibold text-brand-text">Twój gotowy prompt</h3>
                <p className="mt-1 text-[13px] text-brand-sec">
                  Po uzupełnieniu szczegółów kliknij przycisk poniżej. Przygotujemy spójny prompt gotowy do użycia.
                </p>
                <div data-testid={CREATOR.quoteBox} className="mt-4 space-y-2 rounded-field border border-brand-line bg-brand-bg p-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-brand-sec">Wygenerowanie</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-brand-accent"><Coins size={13} /> {genCost} kr.</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-brand-sec">Saldo teraz</span>
                    <span className="text-brand-text">{balance ?? 0} kr.</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-brand-sec">Saldo po wykonaniu</span>
                    <span className="font-semibold text-brand-text">{balanceAfter} kr.</span>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={CREATOR.generateButton}
                  onClick={handleGenerate}
                  disabled={generating}
                  className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-5 py-3 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {generating ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} strokeWidth={2} />}
                  {generating ? "Przygotowuję…" : `Wygeneruj prompt — ${genCost} kredyt`}
                </button>
                <p className="mt-3 inline-flex flex-wrap items-center gap-1.5 text-xs text-brand-muted">
                  <CheckCircle2 size={13} strokeWidth={1.75} className="text-brand-accent" />
                  <span>Twoje odpowiedzi są zapisywane na Twoim koncie, aby umożliwić dostęp do historii. Szczegóły w{" "}
                    <Link to="/polityka-prywatnosci" className="underline hover:text-brand-text">Polityce prywatności</Link>.
                  </span>
                </p>
              </>
            ) : (
              <div data-testid={CREATOR.resultReady}>
                <div className="flex items-center gap-2 text-brand-accent">
                  <CheckCircle2 size={18} strokeWidth={2} />
                  <h3 className="font-display text-[15px] font-semibold text-brand-text">Twój prompt jest gotowy</h3>
                </div>
                <p className="mt-1 text-[13px] text-brand-sec">Wykorzystano {result.cost} kr. Pozostało Ci {result.balance} kr.</p>
                <textarea
                  data-testid={CREATOR.compiledOutput}
                  readOnly
                  value={result.prompt}
                  rows={9}
                  className="mt-3 w-full resize-y rounded-field border border-brand-line bg-brand-bg p-3 text-[13px] leading-relaxed text-brand-text outline-none"
                />
                {(data.safety_notes || []).map((note) => (
                  <p key={note} className="mt-2 inline-flex items-center gap-1.5 text-xs text-brand-muted">
                    <AlertTriangle size={13} strokeWidth={1.75} className="text-brand-accent" /> {note}
                  </p>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    data-testid={CREATOR.copyButton}
                    onClick={handleCopy}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                  >
                    <Copy size={15} strokeWidth={2} /> Kopiuj prompt
                  </button>
                  <button
                    type="button"
                    data-testid={CREATOR.saveButton}
                    onClick={() => saveHistory("standard", result.prompt)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                  >
                    <Save size={15} strokeWidth={1.75} /> Zapisz w bibliotece
                  </button>
                  <button
                    type="button"
                    data-testid={CREATOR.regenerateButton}
                    onClick={() => setResult(null)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-button px-4 py-2 text-xs font-medium text-brand-muted transition-colors hover:text-brand-text"
                  >
                    <RotateCcw size={13} strokeWidth={1.75} /> Zmień dane i wygeneruj ponownie ({genCost} kr.)
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-brand-muted">Kopiowanie i zapis są bezpłatne. Ponowne generowanie po zmianie danych to nowa operacja.</p>
              </div>
            )}
          </div>
        </div>

        {/* Result-only sections: usage → repair → optional upsell */}
        {result && (
          <div ref={resultRef} className="mt-10 space-y-8">
            {/* Usage instruction */}
            <div data-testid={CREATOR.usageCard} className="rounded-panel border border-brand-line bg-brand-surface p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                  <Compass size={18} strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-[15px] font-semibold text-brand-text">Jak użyć promptu?</h3>
                  {usageNote && <p className="mt-1 text-[13px] text-brand-sec">{usageNote}</p>}
                  <ol className="mt-3 space-y-2">
                    {(tool.steps || []).map((step, i) => (
                      <li key={i} className="flex gap-2.5 text-[13px] text-brand-sec">
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-[11px] font-bold text-brand-accent">{i + 1}</span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ol>
                  {tool.notice && (
                    <div className="mt-3 flex items-start gap-2 rounded-card border border-brand-accent/30 bg-brand-accent/10 p-3 text-[13px] text-brand-sec">
                      <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand-accent" />
                      <span>{tool.notice}</span>
                    </div>
                  )}
                  <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                    {openTools.map((r) => (
                      <button
                        key={r.name}
                        type="button"
                        data-testid={`prompt-open-tool-${r.kind}`}
                        onClick={() => copyAndOpenTool(r.url, r.name, r.id)}
                        className="inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                      >
                        <ExternalLink size={15} strokeWidth={2} /> Skopiuj i otwórz {r.name}
                      </button>
                    ))}
                    <button
                      type="button"
                      data-testid="prompt-where-button"
                      onClick={() => setWhereOpen(true)}
                      className="inline-flex items-center justify-center gap-2 rounded-button px-4 py-2.5 text-sm font-medium text-brand-muted transition-colors hover:text-brand-text"
                    >
                      Zobacz wszystkie narzędzia
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Jak użyć w konkretnym narzędziu — instrukcje per tool */}
            {howto.length > 0 && (
              <div data-testid="prompt-howto-card" className="rounded-panel border border-brand-line bg-brand-surface p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                    <Wand2 size={18} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-[15px] font-semibold text-brand-text">Jak użyć w konkretnym narzędziu</h3>
                    <p className="mt-1 text-[13px] text-brand-sec">
                      {requiresImage
                        ? "Wybierz narzędzie do pracy ze zdjęciem. Skopiujemy prompt i otworzymy je w nowej karcie — wystarczy wkleić."
                        : "Wybierz narzędzie. Skopiujemy prompt i otworzymy je w nowej karcie — wystarczy wkleić."}
                    </p>

                    <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                      {howto.map((h) => (
                        <div
                          key={h.id}
                          data-testid={`prompt-howto-${h.id}`}
                          className="flex flex-col rounded-card border border-brand-line bg-brand-bg p-4"
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-brand-text">{h.name}</span>
                            {h.status_label && (
                              <span
                                data-testid={`prompt-howto-status-${h.status}`}
                                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                  h.status === "paid"
                                    ? "bg-amber-500/15 text-amber-300"
                                    : h.status === "requires_account"
                                    ? "bg-brand-elevated text-brand-sec"
                                    : "bg-emerald-500/15 text-emerald-300"
                                }`}
                              >
                                {h.status_label}
                              </span>
                            )}
                          </div>
                          <dl className="mt-3 space-y-2.5 text-[12.5px] leading-relaxed">
                            <div>
                              <dt className="font-semibold text-brand-text">Gdzie wkleić</dt>
                              <dd className="text-brand-sec">{h.where}</dd>
                            </div>
                            <div>
                              <dt className="font-semibold text-brand-text">Co dołączyć</dt>
                              <dd className="text-brand-sec">{h.attach}</dd>
                            </div>
                            <div>
                              <dt className="font-semibold text-brand-text">Jeśli trzeba poprawić</dt>
                              <dd className="text-brand-sec">{h.fix}</dd>
                            </div>
                          </dl>
                          {h.url && (
                            <button
                              type="button"
                              data-testid={`prompt-howto-open-${h.id}`}
                              onClick={() => copyOpenHowto(h.url, h.name, h.id)}
                              className="mt-3.5 inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-3.5 py-2.5 text-[13px] font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                            >
                              <Copy size={14} strokeWidth={2} /> Skopiuj prompt i otwórz {h.name}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {claudeNote && (
                      <div
                        data-testid="prompt-howto-claude-note"
                        className="mt-3 flex items-start gap-2 rounded-card border border-brand-line bg-brand-bg p-3 text-[12.5px] text-brand-sec"
                      >
                        <AlertTriangle size={15} strokeWidth={1.75} className="mt-0.5 shrink-0 text-brand-muted" />
                        <span><strong className="text-brand-text">Claude:</strong> {claudeNote}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Repair instruction */}
            {data.repair_template && (
              <div className="rounded-panel border border-brand-line bg-brand-surface p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                    <LifeBuoy size={18} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-[15px] font-semibold text-brand-text">Nie wyszło tak, jak powinno?</h3>
                    <p className="mt-1 text-[13px] text-brand-sec">
                      Jeżeli rezultat różni się od oczekiwanego, skopiuj poniższą instrukcję i wklej ją w tej samej rozmowie.
                    </p>
                    <textarea
                      data-testid={CREATOR.rescueOutput}
                      readOnly
                      value={result.repairPrompt || ""}
                      rows={6}
                      className="mt-3 w-full resize-y rounded-field border border-brand-line bg-brand-bg p-3 text-[13px] leading-relaxed text-brand-text outline-none"
                    />
                    <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        data-testid={CREATOR.rescueCopyButton}
                        onClick={async () => { if (await copyText(result.repairPrompt || "")) toast.success("Instrukcja poprawy skopiowana."); }}
                        className="inline-flex items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                      >
                        <Copy size={15} strokeWidth={2} /> Kopiuj instrukcję poprawy
                      </button>
                      <button
                        type="button"
                        onClick={() => saveHistory("rescue", result.repairPrompt || "")}
                        className="inline-flex items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                      >
                        <Save size={15} strokeWidth={1.75} /> Zapisz w bibliotece
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Optional upsell — only after the paid result is delivered */}
            <div data-testid={CREATOR.upsellCard} className="rounded-panel border border-dashed border-brand-line bg-brand-surface/60 p-5 sm:p-6">
              <span className="text-[11px] font-bold uppercase tracking-wider text-brand-muted">Opcjonalne</span>
              <h3 className="mt-1 font-display text-[15px] font-semibold text-brand-text">Zrób więcej z tym promptem</h3>
              <p className="mt-1 text-[13px] text-brand-sec">Chcesz od razu otrzymać gotowy tekst? Uruchamiasz to tylko wtedy, gdy tego potrzebujesz.</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <AiActionButton
                  actionKey="short_text"
                  testId={AI.genShort}
                  label="Krótki tekst · 6 kr."
                  icon={Sparkles}
                  buildPayload={() => ({ promptText: result.prompt })}
                  onResult={(out) => setAiOutput(out)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                />
                <AiActionButton
                  actionKey="long_sales_text"
                  testId={AI.genLong}
                  label="Dłuższy materiał · 12 kr."
                  icon={Sparkles}
                  buildPayload={() => ({ promptText: result.prompt })}
                  onResult={(out) => setAiOutput(out)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                />
              </div>
              <p className="mt-2 text-[11px] text-brand-muted">To opcjonalna funkcja. Możesz skopiować prompt i użyć go w wybranym narzędziu bez dodatkowych opłat.</p>

              {aiOutput && (
                <div className="mt-4" data-testid={AI.resultBox}>
                  <textarea
                    readOnly
                    value={aiOutput}
                    rows={8}
                    className="w-full resize-y rounded-field border border-brand-accent/30 bg-brand-bg p-3 text-[13px] leading-relaxed text-brand-text outline-none"
                  />
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={async () => { if (await copyText(aiOutput)) toast.success("Skopiowano wygenerowany tekst."); }}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-button bg-brand-accent px-4 py-2.5 text-sm font-semibold text-brand-on transition-transform hover:-translate-y-0.5"
                    >
                      <Copy size={15} strokeWidth={2} /> Kopiuj tekst
                    </button>
                    <AiActionButton
                      actionKey="improve_input"
                      testId={AI.improveButton}
                      label="Popraw rezultat · 2 kr."
                      icon={Wand2}
                      buildPayload={() => ({ text: aiOutput })}
                      onResult={(out) => setAiOutput(out)}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-button border border-brand-line px-4 py-2.5 text-sm font-semibold text-brand-text transition-colors hover:border-brand-accent/60"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* History */}
        <div className="mt-10">
          <div className="flex items-center gap-2">
            <Clock3 size={18} strokeWidth={1.75} className="text-brand-accent" />
            <h2 className="font-display text-xl font-semibold text-brand-text">Historia użycia</h2>
          </div>
          {history.length === 0 ? (
            <p className="mt-4 rounded-card border border-brand-line bg-brand-surface px-5 py-6 text-sm text-brand-muted">
              Tu pojawią się Twoje zapisane wersje tego promptu — z datą, wybranymi odpowiedziami i użytym tekstem.
            </p>
          ) : (
            <ul data-testid={CREATOR.historyList} className="mt-4 space-y-3">
              {history.map((h) => {
                const params = fields
                  .map((f) => ({ label: f.label, val: fieldDisplayValue(f, h.values?.[f.id]) }))
                  .filter((p) => p.val && String(p.val).trim() !== "");
                return (
                  <li key={h.id} className="rounded-card border border-brand-line bg-brand-surface p-4">
                    <div className="flex items-center justify-between gap-3">
                      <span className="inline-flex items-center gap-2 text-xs font-medium text-brand-muted">
                        {h.variant === "rescue" ? (
                          <span className="inline-flex items-center gap-1 text-brand-accent"><LifeBuoy size={12} /> Poprawa</span>
                        ) : (
                          <span className="inline-flex items-center gap-1"><Sparkles size={12} /> Standard</span>
                        )}
                        {formatDate(h.created_at)}
                      </span>
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await copyText(h.compiled_text);
                          if (ok) toast.success("Skopiowano z historii.");
                        }}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-accent hover:underline"
                      >
                        <Copy size={13} strokeWidth={2} /> Kopiuj
                      </button>
                    </div>
                    {params.length > 0 && (
                      <div className="mt-2.5 flex flex-wrap gap-1.5">
                        {params.map((p) => (
                          <span key={p.label} className="rounded-full border border-brand-line bg-brand-elevated px-2.5 py-1 text-[11px] text-brand-sec">
                            <span className="text-brand-muted">{p.label}:</span> {p.val}
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 line-clamp-2 text-[13px] text-brand-sec">{h.compiled_text}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* "Gdzie użyć?" modal — full tool list */}
      <Dialog open={whereOpen} onOpenChange={setWhereOpen}>
        <DialogContent data-testid={CREATOR.toolInstruction} className="max-w-lg border-brand-line bg-brand-surface text-brand-text">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display text-brand-text">
              <Compass size={18} strokeWidth={1.75} className="text-brand-accent" /> Gdzie użyć promptu?
            </DialogTitle>
            <DialogDescription className="text-brand-sec">
              {requiresImage
                ? "Ten prompt działa na Twoim zdjęciu. Kliknij narzędzie — skopiujemy prompt i otworzymy je w nowej karcie."
                : "Kliknij narzędzie — skopiujemy prompt i otworzymy je w nowej karcie."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5">
            {recs.map((r) => {
              const isButton = !!r.url;
              const Row = isButton ? "button" : "div";
              const rowProps = isButton ? { type: "button", onClick: () => copyAndOpenTool(r.url, r.name, r.id) } : {};
              return (
                <Row
                  key={r.name}
                  {...rowProps}
                  data-testid={`prompt-tool-${r.kind}`}
                  className={`flex w-full items-start gap-3 rounded-card border border-brand-line bg-brand-bg p-3 text-left ${isButton ? "transition-colors hover:border-brand-accent/60" : ""}`}
                >
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
                    {r.kind === "alternative" ? <Star size={15} strokeWidth={1.75} /> : <Wand2 size={15} strokeWidth={1.75} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-brand-text">{r.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                        r.kind === "alternative" ? "bg-brand-elevated text-brand-muted" : "bg-brand-accent/15 text-brand-accent"
                      }`}>
                        {r.kind === "alternative" ? "Alternatywa" : "Zalecane"}
                      </span>
                      {r.status_label && (
                        <span
                          data-testid={`prompt-tool-status-${r.status}`}
                          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            r.status === "paid"
                              ? "bg-amber-500/15 text-amber-300"
                              : r.status === "requires_account"
                              ? "bg-brand-elevated text-brand-sec"
                              : "bg-emerald-500/15 text-emerald-300"
                          }`}
                        >
                          {r.status_label}
                        </span>
                      )}
                    </span>
                    {r.note && <span className="mt-0.5 block text-[13px] text-brand-sec">{r.note}</span>}
                    {isButton && (
                      <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-accent">
                        <Copy size={12} strokeWidth={2} /> Skopiuj i otwórz {r.name}
                      </span>
                    )}
                  </span>
                  {isButton && <ExternalLink size={15} strokeWidth={2} className="mt-1 shrink-0 text-brand-muted" />}
                </Row>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
